/**
 * Secondary sidebar listing the user's conversations.
 *
 * Real-time via `useConversations`. A conversa ativa é destacada; "Nova conversa"
 * leva ao chat home (`/`), onde a primeira mensagem cria a conversa no backend.
 *
 * As conversas podem ser organizadas em pastas (`useFolders`, carregado aqui
 * dentro para não mexer nos call sites) e excluídas de forma SUAVE: a exclusão
 * apenas some da lista — o histórico é mantido no Firestore (ver
 * `softDeleteConversation`). Pastas aparecem como seções colapsáveis; conversas
 * sem pasta vêm primeiro, sem cabeçalho de seção (comportamento preservado para
 * quem não usa pastas).
 */
import type { ConversationDoc, FolderDoc } from '@agente/shared'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ChevronRight,
  Folder,
  FolderInput,
  FolderPlus,
  Loader,
  MessageCircle,
  MessageSquarePlus,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import type { UseConversationsResult } from '@/hooks/useConversations'
import { useFolders } from '@/hooks/useFolders'
import { useAuth } from '@/lib/auth'
import {
  createFolder,
  deleteFolder,
  renameFolder,
  setConversationFolder,
  softDeleteConversation,
} from '@/lib/firestore'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'

/** Tamanho máximo do nome de uma pasta. */
const MAX_FOLDER_NAME = 40

interface ConversationListProps extends UseConversationsResult {
  activeId: string | null
  onNavigate?: () => void
}

export function ConversationList({
  conversations,
  loading,
  activeId,
  onNavigate,
}: ConversationListProps) {
  const { folders } = useFolders()

  // Estado dos diálogos (um por vez). Guardamos o alvo (conversa/pasta) em vez de
  // um booleano para preencher os textos pt-BR do diálogo.
  const [deleting, setDeleting] = useState<ConversationDoc | null>(null)
  const [movingToNewFolder, setMovingToNewFolder] = useState<ConversationDoc | null>(null)
  const [renamingFolder, setRenamingFolder] = useState<FolderDoc | null>(null)
  const [deletingFolder, setDeletingFolder] = useState<FolderDoc | null>(null)

  // Colapso das seções de pasta — estado local, não persiste (por decisão).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  // Agrupa em memória: conversas sem pasta (ou órfãs de pasta inexistente) primeiro,
  // depois uma seção por pasta. A ordenação por `updatedAt` desc já vem do hook.
  const { ungrouped, byFolder } = useMemo(() => {
    const folderIds = new Set(folders.map((f) => f.id))
    const ungroupedList: ConversationDoc[] = []
    const grouped = new Map<string, ConversationDoc[]>()
    for (const conv of conversations) {
      if (conv.folderId && folderIds.has(conv.folderId)) {
        const list = grouped.get(conv.folderId) ?? []
        list.push(conv)
        grouped.set(conv.folderId, list)
      } else {
        ungroupedList.push(conv)
      }
    }
    return { ungrouped: ungroupedList, byFolder: grouped }
  }, [conversations, folders])

  const isEmpty = conversations.length === 0 && folders.length === 0

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="px-1">
        <Button asChild variant="outline" className="w-full justify-start gap-2">
          <Link to="/" onClick={onNavigate} activeOptions={{ exact: true }}>
            <MessageSquarePlus className="size-4" />
            Nova conversa
          </Link>
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="flex flex-col gap-0.5 pr-2">
          {loading ? (
            <div className="space-y-1.5 px-1 pt-1">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          ) : isEmpty ? (
            <p className="text-muted-foreground px-2 py-6 text-center text-xs">
              Nenhuma conversa ainda.
            </p>
          ) : (
            <>
              {ungrouped.map((conv) => (
                <ConversationRow
                  key={conv.id}
                  conv={conv}
                  activeId={activeId}
                  folders={folders}
                  onNavigate={onNavigate}
                  onMoveToNewFolder={() => setMovingToNewFolder(conv)}
                  onDelete={() => setDeleting(conv)}
                />
              ))}

              {folders.map((folder) => (
                <FolderSection
                  key={folder.id}
                  folder={folder}
                  conversations={byFolder.get(folder.id) ?? []}
                  activeId={activeId}
                  folders={folders}
                  collapsed={collapsed[folder.id] ?? false}
                  onToggle={() =>
                    setCollapsed((prev) => ({ ...prev, [folder.id]: !(prev[folder.id] ?? false) }))
                  }
                  onNavigate={onNavigate}
                  onMoveToNewFolder={(conv) => setMovingToNewFolder(conv)}
                  onDeleteConversation={(conv) => setDeleting(conv)}
                  onRenameFolder={() => setRenamingFolder(folder)}
                  onDeleteFolder={() => setDeletingFolder(folder)}
                />
              ))}
            </>
          )}
        </div>
      </ScrollArea>

      <DeleteConversationDialog
        conversation={deleting}
        activeId={activeId}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
      />
      <NewFolderDialog
        conversation={movingToNewFolder}
        onOpenChange={(next) => {
          if (!next) setMovingToNewFolder(null)
        }}
      />
      <RenameFolderDialog
        folder={renamingFolder}
        onOpenChange={(next) => {
          if (!next) setRenamingFolder(null)
        }}
      />
      <DeleteFolderDialog
        folder={deletingFolder}
        onOpenChange={(next) => {
          if (!next) setDeletingFolder(null)
        }}
      />
    </div>
  )
}

function FolderSection({
  folder,
  conversations,
  activeId,
  folders,
  collapsed,
  onToggle,
  onNavigate,
  onMoveToNewFolder,
  onDeleteConversation,
  onRenameFolder,
  onDeleteFolder,
}: {
  folder: FolderDoc
  conversations: ConversationDoc[]
  activeId: string | null
  folders: FolderDoc[]
  collapsed: boolean
  onToggle: () => void
  onNavigate?: () => void
  onMoveToNewFolder: (conv: ConversationDoc) => void
  onDeleteConversation: (conv: ConversationDoc) => void
  onRenameFolder: () => void
  onDeleteFolder: () => void
}) {
  return (
    <div className="mt-1">
      <div className="group/folder relative flex items-center">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="text-muted-foreground hover:text-sidebar-foreground flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors"
        >
          <ChevronRight
            className={cn('size-3.5 shrink-0 transition-transform', !collapsed && 'rotate-90')}
            aria-hidden="true"
          />
          <Folder className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-left">{folder.name}</span>
          <span className="shrink-0 tabular-nums">{conversations.length}</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Ações da pasta ${folder.name}`}
              className="text-muted-foreground size-6 shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/folder:opacity-100 data-[state=open]:opacity-100 pointer-coarse:opacity-100"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onSelect={onRenameFolder}>
              <Pencil className="size-4" />
              Renomear pasta
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onSelect={onDeleteFolder}>
              <Trash2 className="size-4" />
              Excluir pasta
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {collapsed ? null : conversations.length === 0 ? (
        <p className="text-muted-foreground px-2 py-1.5 pl-8 text-[0.6875rem] italic">
          Pasta vazia
        </p>
      ) : (
        <div className="flex flex-col gap-0.5">
          {conversations.map((conv) => (
            <ConversationRow
              key={conv.id}
              conv={conv}
              activeId={activeId}
              folders={folders}
              onNavigate={onNavigate}
              onMoveToNewFolder={() => onMoveToNewFolder(conv)}
              onDelete={() => onDeleteConversation(conv)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ConversationRow({
  conv,
  activeId,
  folders,
  onNavigate,
  onMoveToNewFolder,
  onDelete,
}: {
  conv: ConversationDoc
  activeId: string | null
  folders: FolderDoc[]
  onNavigate?: () => void
  onMoveToNewFolder: () => void
  onDelete: () => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const isActive = conv.id === activeId

  async function moveTo(folderId: string | null) {
    if (!uid) return
    try {
      await setConversationFolder(uid, conv.id, folderId)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível mover a conversa', { description: message })
    }
  }

  // Pastas de destino, ocultando a pasta atual (mover para a mesma pasta é no-op).
  const targetFolders = folders.filter((f) => f.id !== conv.folderId)

  return (
    <div className="group/conv relative">
      <Link
        to="/conversations/$id"
        params={{ id: conv.id }}
        onClick={onNavigate}
        className={cn(
          'flex flex-col gap-0.5 rounded-md py-2 pr-9 pl-2 text-sm transition-colors',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/80',
        )}
        aria-current={isActive ? 'page' : undefined}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium">{conv.title || 'Sem título'}</span>
          {conv.channel === 'whatsapp' ? (
            <span
              className="text-muted-foreground inline-flex shrink-0"
              title="Conversa vinculada ao WhatsApp"
            >
              <MessageCircle className="size-3.5" />
            </span>
          ) : null}
        </span>
        <span className="text-muted-foreground flex items-center justify-between gap-2 text-[0.6875rem]">
          {conv.lastPreview ? (
            <span className="min-w-0 flex-1 truncate">{conv.lastPreview}</span>
          ) : (
            <span className="min-w-0 flex-1 truncate italic">Sem mensagens</span>
          )}
          <span className="shrink-0 whitespace-nowrap">{formatRelative(conv.updatedAt)}</span>
        </span>
      </Link>

      <div className="absolute top-1.5 right-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Ações da conversa ${conv.title || 'Sem título'}`}
              className="text-muted-foreground size-7 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/conv:opacity-100 data-[state=open]:opacity-100 pointer-coarse:opacity-100"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FolderInput className="size-4" />
                Mover para pasta
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48">
                {conv.folderId ? (
                  <DropdownMenuItem onSelect={() => void moveTo(null)}>Sem pasta</DropdownMenuItem>
                ) : null}
                {targetFolders.map((folder) => (
                  <DropdownMenuItem key={folder.id} onSelect={() => void moveTo(folder.id)}>
                    <Folder className="size-4" />
                    <span className="truncate">{folder.name}</span>
                  </DropdownMenuItem>
                ))}
                {targetFolders.length > 0 || conv.folderId ? <DropdownMenuSeparator /> : null}
                <DropdownMenuItem onSelect={onMoveToNewFolder}>
                  <FolderPlus className="size-4" />
                  Nova pasta…
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={onDelete}>
              <Trash2 className="size-4" />
              Excluir conversa
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

function DeleteConversationDialog({
  conversation,
  activeId,
  onOpenChange,
}: {
  conversation: ConversationDoc | null
  activeId: string | null
  onOpenChange: (open: boolean) => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const navigate = useNavigate()
  const [deleting, setDeleting] = useState(false)

  async function handleDelete() {
    if (!conversation || !uid) return
    setDeleting(true)
    try {
      await softDeleteConversation(uid, conversation.id)
      toast.success('Conversa excluída')
      onOpenChange(false)
      // Ao excluir a conversa ATIVA, sai da rota dela (ela some da lista).
      if (conversation.id === activeId) {
        await navigate({ to: '/' })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível excluir a conversa', { description: message })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={conversation !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir conversa</DialogTitle>
          <DialogDescription>
            {conversation
              ? `A conversa "${conversation.title || 'Sem título'}" sai da sua lista. Ela é mantida internamente como histórico.`
              : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Loader className="size-4 animate-spin" /> : null}
            Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function NewFolderDialog({
  conversation,
  onOpenChange,
}: {
  conversation: ConversationDoc | null
  onOpenChange: (open: boolean) => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  const trimmed = name.trim()
  const canSave = trimmed.length > 0 && !saving

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSave || !uid || !conversation) return
    setSaving(true)
    try {
      const folderId = await createFolder(uid, trimmed)
      await setConversationFolder(uid, conversation.id, folderId)
      toast.success('Pasta criada')
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível criar a pasta', { description: message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      // Re-monta ao abrir para o input começar vazio a cada uso.
      key={conversation?.id ?? 'new-folder-closed'}
      open={conversation !== null}
      onOpenChange={onOpenChange}
    >
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Nova pasta</DialogTitle>
            <DialogDescription>
              A conversa selecionada será movida para a nova pasta.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-folder-name">Nome da pasta</Label>
            <Input
              id="new-folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Clientes, Contratos, Pessoal"
              maxLength={MAX_FOLDER_NAME}
              required
              autoFocus
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSave}>
              {saving ? <Loader className="size-4 animate-spin" /> : null}
              Criar e mover
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function RenameFolderDialog({
  folder,
  onOpenChange,
}: {
  folder: FolderDoc | null
  onOpenChange: (open: boolean) => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const [name, setName] = useState(folder?.name ?? '')
  const [saving, setSaving] = useState(false)

  const trimmed = name.trim()
  const canSave = trimmed.length > 0 && !saving

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSave || !uid || !folder) return
    setSaving(true)
    try {
      await renameFolder(uid, folder.id, trimmed)
      toast.success('Pasta renomeada')
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível renomear a pasta', { description: message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      // Re-monta por pasta para o input carregar o nome atual.
      key={folder?.id ?? 'rename-folder-closed'}
      open={folder !== null}
      onOpenChange={onOpenChange}
    >
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Renomear pasta</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rename-folder-name">Nome da pasta</Label>
            <Input
              id="rename-folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_FOLDER_NAME}
              required
              autoFocus
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSave}>
              {saving ? <Loader className="size-4 animate-spin" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DeleteFolderDialog({
  folder,
  onOpenChange,
}: {
  folder: FolderDoc | null
  onOpenChange: (open: boolean) => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const [deleting, setDeleting] = useState(false)

  async function handleDelete() {
    if (!folder || !uid) return
    setDeleting(true)
    try {
      await deleteFolder(uid, folder.id)
      toast.success('Pasta excluída')
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível excluir a pasta', { description: message })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={folder !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir pasta</DialogTitle>
          <DialogDescription>
            {folder
              ? `A pasta "${folder.name}" será excluída. As conversas dela voltam para "Sem pasta" — nenhuma conversa é excluída.`
              : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Loader className="size-4 animate-spin" /> : null}
            Excluir pasta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
